import { Box, Card } from "@mui/material";
import * as styles from "./FeatureManage.module.scss";
import React, { Suspense, useCallback, useEffect, useState } from "react";
import {
  deleteFeatureInFeatureGroup,
  featureGroupFeaturesReorder,
  featureGroupFeatureVisibilityToggle,
  getFeaturesByGroupId,
} from "../../services/RequestAccess/RequestAccess";
import { useParams } from "react-router-dom";
import { triggerToast } from "../../utils/toast";
import PermissionElement from "../../Components/PermissionManage/PermissionElement";
import DragablePermissionElement from "../../Components/PermissionManage/DragablePermissionElement";
import NoAvailablePermission from "../../Components/PermissionManage/NoAvailablePermission";
import {
  Accordion,
  LoadingSpinner,
  Search,
} from "cprime-foundational-components";
import { base64PrivateKey, getEnv } from "../../Constants/env";
import { useIdToken } from "../../hooks/useAuthSession";
import axios from "axios";
import { decodePolicyData } from "../../utils/commonUtil";

const sortByRank = (a: any, b: any) => a?.rank - b?.rank;

const filteredTasks = (data: any, searchString: any) => {
  return data?.filter((item: any) =>
    item["permission_name"]
      ?.toLowerCase()
      ?.includes(searchString?.toLowerCase()),
  );
};

const initialExpandedPanelsState = ["Visible Features", "Hidden Features"];

function FeatureManage({ title, openPanel, setOpenPanel, triggerEffect }: any) {
  const scssClass = styles as any;
  const { id = "79" } = useParams();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [searchString, setSearchString] = useState("");
  const [expandedPanels, setExpandedPanels] = useState<Array<string>>(
    initialExpandedPanelsState,
  );
  const [tasks, setTasks] = useState<any>(() => {
    const initialTasks = {
      permission: [],
      unavailable: [],
    };
    return initialTasks;
  });

  const [activeCard, setActiveCard] = useState<string | null>(null);
  const [isAccessible, setIsAccessible] = useState(false);

  const mapFeatureToPermission = (
    feature: any,
    status: "available" | "unavailable",
  ) => ({
    permission_id: feature?.id,
    permission_name: feature?.feature_name,
    description: feature?.feature_description,
    rank: feature?.sequence,
    app_name: feature?.app_name,
    added_on: feature?.added_on,
    status,
    is_open_to_all: feature?.is_open_to_all,
  });

  // const envtype = getEnv()?.envtype;
  const idToken = useIdToken();

  // const isEditable = useMemo(
  //   () => envtype !== "prod" && envtype !== "qa",
  //   [envtype],
  // );
  const isEditable = true; // As per new requirement, all environments are editable but changes in prod and qa should be made in lower env and promoted. So showing warning message for prod and qa env in UI.

  const checkIsAccessible = useCallback(async () => {
    if (!idToken) return false;
    try {
      const { data, status } = await axios.get("/v1/roleconsumption/policy", {
        baseURL: getEnv()?.baseURL,
        headers: {
          Authorization: idToken,
        },
      });
      if (status !== 200) return false;

      const decodeData = decodePolicyData(data?.data, base64PrivateKey);
      const accessList = Array.isArray(decodeData)
        ? decodeData.flatMap((item: any) => item?.Access ?? [])
        : (decodeData?.Access ?? []);

      return accessList.some((access: any) =>
        (access?.feature ?? []).some(
          (feature: any) =>
            feature?.FeatureName === "App Management" &&
            feature?.FeaturePermission?.some(
              (permission: any) => permission?.Permission === "Edit",
            ),
        ),
      );
    } catch (error) {
      // console.error("Error checking access:", error);
      return false;
    }
  }, [idToken]);

  useEffect(() => {
    let isMounted = true;

    const loadAccess = async () => {
      const hasAccess = await checkIsAccessible();
      if (isMounted) {
        setIsAccessible(hasAccess);
      }
    };

    loadAccess();

    return () => {
      isMounted = false;
    };
  }, [checkIsAccessible]);

  const getFeaturesById = async () => {
    setIsLoading(true);
    try {
      const response = await getFeaturesByGroupId(Number(id));

      if (response?.status !== "SUCCESS") {
        triggerToast(
          response?.error || response?.message || "Failed to fetch features",
          "error",
        );
        return;
      }

      const visibleFeatures =
        response?.data?.visible?.map((f: any) =>
          mapFeatureToPermission(f, "available"),
        ) || [];

      const hiddenFeatures =
        response?.data?.hidden?.map((f: any) =>
          mapFeatureToPermission(f, "unavailable"),
        ) || [];

      setTasks({
        permission: visibleFeatures.sort(sortByRank),
        unavailable: hiddenFeatures.sort(sortByRank),
      });
    } catch (err) {
      triggerToast("Something went wrong while fetching features", "error");
    } finally {
      setIsLoading(false);
    }
  };

  const onDrop = async (position: number) => {
    if (!activeCard) return;
    const [type, indexStr] = activeCard.split("-");
    const list = type === "feature" ? tasks?.permission : tasks?.unavailable;
    const task = list?.[+indexStr];

    if (!task) return;

    const isDraggingUp = +indexStr > position;
    const adjustedPosition = isDraggingUp
      ? position
      : Math.max(position - 1, 0);
    const newPosition = list?.[adjustedPosition];

    const applicationId = Number(id);
    if (
      !applicationId ||
      !task?.permission_id ||
      !newPosition?.rank ||
      newPosition?.rank === task?.rank
    )
      return;

    const payload = {
      group_id: applicationId,
      moves: [
        {
          feature_id: task?.permission_id,
          old_sequence: task?.rank,
          new_sequence: newPosition?.rank,
        },
      ],
    };
    const res = await featureGroupFeaturesReorder(payload);
    if (res?.status !== "SUCCESS") {
      triggerToast(
        res?.error || res?.message || "Failed to update rank",
        "error",
      );
      return;
    }
    if (res?.status === "SUCCESS") {
      getFeaturesById();
      triggerToast(res?.message, res?.error ? "error" : "success");
    }
  };

  const handleAccordionOnChange = (value: string) => {
    if (expandedPanels?.includes(value)) {
      setExpandedPanels(expandedPanels?.filter((d) => d !== value));
    } else {
      setExpandedPanels([...expandedPanels, value]);
    }
  };

  const filteredPermissions = React.useMemo(
    () => filteredTasks(tasks?.permission, searchString),
    [tasks, searchString],
  );

  const filteredUnavailable = React.useMemo(
    () => filteredTasks(tasks?.unavailable, searchString),
    [tasks, searchString],
  );

  const deletePermissionConfirm = async (permission: any, cb: any) => {
    const groupId = Number(id);
    const payload = {
      group_id: groupId,
      feature_id: permission?.permission_id,
    };

    if (groupId) {
      try {
        const res = await deleteFeatureInFeatureGroup(payload);
        if (res?.status === "SUCCESS") {
          setTasks((prevTasks: any) => {
            const updatedPermission = prevTasks.permission.filter(
              (feature: any) =>
                feature.permission_id !== permission.permission_id,
            );
            const updatedUnavailable = prevTasks.unavailable.filter(
              (feature: any) =>
                feature.permission_id !== permission.permission_id,
            );

            return {
              permission: updatedPermission.sort(sortByRank),
              unavailable: updatedUnavailable.sort(sortByRank),
            };
          });
          const toastType = res?.error ? "error" : "success";
          triggerToast(res?.message, toastType);
          cb?.();
        } else {
          triggerToast(
            res?.error ? res?.message : "Failed to Delete Feature.",
            "error",
          );
        }
      } catch (error) {
        // console.error("Error during deletion:", error);
      }
    }
  };

  useEffect(() => {
    getFeaturesById();
  }, [triggerEffect]);

  const handleStatusChange = async (
    permissionId: number,
    newStatus: boolean,
  ) => {
    const payload = {
      feature_id: permissionId,
      is_open_to_all: newStatus,
      group_id: Number(id),
    };
    const res = await featureGroupFeatureVisibilityToggle(payload);
    if (res?.status === "SUCCESS") {
      getFeaturesById();
      const toastType = res?.error ? "error" : "success";
      triggerToast(res?.message, toastType);
    } else {
      triggerToast(
        res?.error ? res?.message : "Failed to Delete Feature.",
        "error",
      );
    }
  };

  return (
    <div
      style={{
        width: "100%",
        maxHeight: "calc(100vh - 322px)",
        overflow: "auto",
        marginTop: "16px",
      }}
    >
      <Card
        variant="outlined"
        className={scssClass?.card}
        sx={{ border: "none" }}
      >
        <div className={scssClass?.cardheader}>
          <span className={scssClass?.cardtitle}>{title}</span>
          <Search
            placeholder="Search Features"
            autoFocus={true}
            value={searchString}
            onChange={(e) => setSearchString(e)}
            onSubmit={() => {}}
            onClear={() => setSearchString("")}
            sx={{
              height: "40px",
              width: "318px",
              input: { fontFamily: "ToyotaType-Regular" },
            }}
          />
        </div>
        <div>
          {isLoading ? (
            <Box
              sx={{
                textAlign: "center",
                padding: "24px",
                fontSize: "16px",
                fontWeight: 500,
              }}
            >
              <LoadingSpinner fullScreen={true} />
            </Box>
          ) : (
            <>
              {[...initialExpandedPanelsState]?.map((sectionName) => {
                return (
                  <Accordion
                    key={sectionName}
                    title={sectionName}
                    // panel={sectionName}
                    sx={{
                      width: "100%",
                      fontSize: "16px",
                      fontWeight: "400",
                      boxShadow: "unset",
                      marginTop: "0px !important",
                      border: "none",
                      ".MuiTypography-body1": {
                        p: {
                          fontSize: "16px",
                          fontWeight: "400",
                          fontFamily: "ToyotaType-Regular",
                        },
                      },
                      ".expand-more-icon": {
                        width: "24px",
                        height: "100%",
                      },
                    }}
                    expanded={expandedPanels?.includes(sectionName)}
                    onChange={() => handleAccordionOnChange(sectionName)}
                  >
                    <div data-testid={`${sectionName}`}>
                      {sectionName === initialExpandedPanelsState[0] ? (
                        filteredPermissions?.length > 0 ? (
                          filteredPermissions?.map((d: any, index: any) => (
                            <Suspense key={d?.permission_id}>
                              <div data-testid={index} key={d?.permission_id}>
                                {index === 0 && (
                                  <DragablePermissionElement
                                    onDrop={(p: number) => onDrop(0)}
                                    type="feature"
                                    item={activeCard}
                                    index={index}
                                  />
                                )}
                                <PermissionElement
                                  permission={d}
                                  onStatusChange={handleStatusChange}
                                  setActiveCard={(eleIndex: any) => {
                                    if (eleIndex || eleIndex === 0) {
                                      setActiveCard(`feature-${index}`);
                                    } else {
                                      setActiveCard("");
                                    }
                                  }}
                                  index={index}
                                  label={d?.permission_name}
                                  isActive={true}
                                  isAvailable={d?.status === "available"}
                                  isEditable={false}
                                  isEditAllow={isEditable && isAccessible}
                                  isDeletable={false}
                                  isArchive={true}
                                  isUnarchive={true}
                                  deletePermissionConfirm={
                                    deletePermissionConfirm
                                  }
                                />
                                <DragablePermissionElement
                                  onDrop={(p: number) => onDrop(index + 1)}
                                  type="feature"
                                  item={activeCard}
                                  index={index}
                                />
                              </div>
                            </Suspense>
                          ))
                        ) : (
                          <NoAvailablePermission type="Available Features" />
                        )
                      ) : filteredUnavailable?.length > 0 ? (
                        filteredUnavailable?.map((d: any, index: any) => (
                          <Suspense key={d?.permission_id || d?.name}>
                            <div data-testid={index}>
                              {index === 0 && (
                                <DragablePermissionElement
                                  onDrop={(p: number) => onDrop(0)}
                                  type="unavailable"
                                  item={activeCard}
                                  index={index}
                                />
                              )}
                              {tasks.permission.length === 0 &&
                              tasks.unavailable.length === 0 ? (
                                <p>No unavailable features</p>
                              ) : (
                                <PermissionElement
                                  permission={d}
                                  onStatusChange={handleStatusChange}
                                  setActiveCard={(eleIndex: any) => {
                                    if (eleIndex || eleIndex === 0) {
                                      setActiveCard(`unavailable-${index}`);
                                    } else {
                                      setActiveCard("");
                                    }
                                  }}
                                  index={index}
                                  label={d?.permission_name}
                                  isActive={true}
                                  isAvailable={d?.status === "available"}
                                  isEditable={false}
                                  isEditAllow={isEditable && isAccessible}
                                  isDeletable={true}
                                  canDelete={true}
                                  distributorname={d?.distributor}
                                  deletePermissionConfirm={
                                    deletePermissionConfirm
                                  }
                                />
                              )}

                              <DragablePermissionElement
                                onDrop={(p: number) => onDrop(index + 1)}
                                type="unavailable"
                                item={activeCard}
                                index={index}
                              />
                            </div>
                          </Suspense>
                        ))
                      ) : (
                        <NoAvailablePermission type="Unavailable Features" />
                      )}
                    </div>
                  </Accordion>
                );
              })}
            </>
          )}
        </div>
      </Card>
    </div>
  );
}

export default FeatureManage;
